import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "@/components/PageHeader";
import {
  BACKEND_HEALTH_CHECKS,
  probeBackendHealth,
} from "@/core/integrations/backend-health";
import {
  MODULE_INTEGRATIONS,
  probeModuleOrigin,
  type ModuleIntegrationRow,
} from "@/core/integrations/module-handoff";
import type { IntegratedModuleId } from "@/core/config";
import { routes } from "@/router/all_routes";

type ProbeState = Record<IntegratedModuleId, { loading: boolean; reachable: boolean; detail: string }>;
type BackendProbeState = Record<string, { loading: boolean; reachable: boolean; detail: string }>;

export function AdminIntegrationsPage() {
  const [probes, setProbes] = useState<ProbeState>(() =>
    Object.fromEntries(
      MODULE_INTEGRATIONS.map((row) => [
        row.id,
        { loading: true, reachable: false, detail: "Vérification…" },
      ]),
    ) as ProbeState,
  );

  const [backendProbes, setBackendProbes] = useState<BackendProbeState>(() =>
    Object.fromEntries(
      BACKEND_HEALTH_CHECKS.map((row) => [
        row.id,
        { loading: true, reachable: false, detail: "Vérification…" },
      ]),
    ),
  );

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const results = await Promise.all(
        MODULE_INTEGRATIONS.map(async (row) => {
          const result = await probeModuleOrigin(row.id);
          return [row.id, { loading: false, ...result }] as const;
        }),
      );
      if (cancelled) return;
      setProbes(Object.fromEntries(results) as ProbeState);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const results = await Promise.all(
        BACKEND_HEALTH_CHECKS.map(async (row) => {
          const result = await probeBackendHealth(row.healthUrl);
          return [row.id, { loading: false, ...result }] as const;
        }),
      );
      if (cancelled) return;
      setBackendProbes(Object.fromEntries(results));
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div>
      <PageHeader
        title="Intégrations cross-modules"
        description="Handoff JWT, embed iframe et disponibilité des modules ITaaS (RH, stock, billing)."
      />

      <div className="ph-dash-card mb-4">
        <p className="small text-muted mb-3">
          Chaque module externe expose une API stable ; le shell Hospitol compose l&apos;interface
          native (lexique métier, graphiques) et réserve l&apos;iframe à la{" "}
          <strong>console avancée</strong> du module source.
        </p>
        <div className="table-responsive">
          <table className="table table-sm ph-dash-table ph-diag-table align-middle mb-0">
            <thead>
              <tr>
                <th>Module</th>
                <th>URL</th>
                <th>Statut</th>
                <th>Test Hospitol</th>
              </tr>
            </thead>
            <tbody>
              {MODULE_INTEGRATIONS.map((row) => (
                <IntegrationRow key={row.id} row={row} probe={probes[row.id]} />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="ph-dash-card mb-4">
        <h3 className="h6 mb-3">APIs backend (proxy Vite)</h3>
        <div className="table-responsive">
          <table className="table table-sm ph-dash-table ph-diag-table align-middle mb-0">
            <thead>
              <tr>
                <th>Service</th>
                <th>Health</th>
                <th>Statut</th>
              </tr>
            </thead>
            <tbody>
              {BACKEND_HEALTH_CHECKS.map((row) => {
                const probe = backendProbes[row.id];
                return (
                  <tr key={row.id} className="ph-diag-row">
                    <td className="fw-semibold" data-label="Service">
                      {row.label}
                    </td>
                    <td className="small text-muted" data-label="Health">
                      {row.healthUrl}
                    </td>
                    <td data-label="Statut">
                      {probe?.loading ? (
                        <span className="badge bg-secondary">…</span>
                      ) : probe?.reachable ? (
                        <span className="badge bg-success">OK</span>
                      ) : (
                        <span className="badge bg-warning text-dark">Hors ligne</span>
                      )}
                      <div className="small text-muted mt-1">{probe?.detail}</div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="ph-dash-card">
        <h3 className="h6 mb-2">Checklist démo demo-hospitol</h3>
        <ol className="small text-muted mb-0 ps-3">
          <li>
            <code>.\scripts\seed-hospitol-demo.ps1</code> — IAM + ISC + Billing
          </li>
          <li>
            <code>.\scripts\start-hospitol.ps1 -DirectHospitol</code> — stack complète
          </li>
          <li>
            Médecin <code>doctor@hospital.demo</code> — consultation démo avec Paracétamol (auto-lié au stock)
          </li>
          <li>
            Pharmacien <code>pharmacy@hospital.demo</code> — dispensation + sync billing
          </li>
          <li>Parcours détaillé : <code>docs/TEST-PARCOURS.md</code></li>
        </ol>
        <div className="mt-3">
          <Link to={routes.adminSettings} className="btn btn-sm btn-outline-secondary">
            Paramètres établissement
          </Link>
        </div>
      </div>
    </div>
  );
}

function IntegrationRow({
  row,
  probe,
}: {
  row: ModuleIntegrationRow;
  probe: ProbeState[IntegratedModuleId];
}) {
  return (
    <tr className="ph-diag-row">
      <td data-label="Module">
        <div className="fw-semibold">{row.label}</div>
        {row.notes && <div className="small text-muted">{row.notes}</div>}
      </td>
      <td className="small" data-label="URL">
        <div>{row.baseUrl}</div>
        <div className="text-muted">{row.sampleEmbedPath}</div>
      </td>
      <td data-label="Statut">
        {probe?.loading ? (
          <span className="badge bg-secondary">…</span>
        ) : probe?.reachable ? (
          <span className="badge bg-success">Joignable</span>
        ) : (
          <span className="badge bg-warning text-dark">Hors ligne</span>
        )}
        <div className="small text-muted mt-1">{probe?.detail}</div>
      </td>
      <td data-label="Test Hospitol">
        <div className="d-flex gap-1 flex-wrap ph-integration-actions">
          <Link to={row.hospitolRoute} className="btn btn-sm ph-btn-outline">
            Parcours natif
          </Link>
          {row.adminConsoleRoute && (
            <Link to={row.adminConsoleRoute} className="btn btn-sm btn-outline-secondary">
              Console
            </Link>
          )}
        </div>
      </td>
    </tr>
  );
}
